import { useCallback, type FormEvent } from 'react'
import { useCreateAgencyMutation } from '@/modules/agencies/api/agenciesApi'
import { slugifyAgencyName } from '@/modules/agencies/utils/slugifyAgencyName'
import { validateAgencyCreate } from '@/modules/agencies/utils/validateAgencyCreate'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectAgencyFormFieldErrors,
  selectAgencyFormFields,
  selectAgencyFormSubmitError,
  selectIsAgencyFormSubmitting,
  selectIsCreateAgencyOpen,
} from '@/store/selectors/agenciesUiSelectors'
import {
  agencyFormFailed,
  agencyFormFieldChanged,
  agencyFormSlugSynced,
  agencyFormSubmitted,
  agencyFormSucceeded,
  agencyFormValidationFailed,
  createAgencyClosed,
  createAgencyOpened,
} from '@/store/slices/agenciesUiSlice'
import { getAuthErrorMessage } from '@/utils/getAuthErrorMessage'

export function useAgencyForm() {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsCreateAgencyOpen)
  const fields = useAppSelector(selectAgencyFormFields)
  const fieldErrors = useAppSelector(selectAgencyFormFieldErrors)
  const submitError = useAppSelector(selectAgencyFormSubmitError)
  const isSubmitting = useAppSelector(selectIsAgencyFormSubmitting)
  const [createAgency] = useCreateAgencyMutation()

  const openCreate = useCallback(() => {
    dispatch(createAgencyOpened())
  }, [dispatch])

  const closeCreate = useCallback(() => {
    if (isSubmitting) {
      return
    }
    dispatch(createAgencyClosed())
  }, [dispatch, isSubmitting])

  const onNameChange = useCallback(
    (value: string) => {
      dispatch(agencyFormFieldChanged({ field: 'name', value }))
      dispatch(agencyFormSlugSynced(slugifyAgencyName(value)))
    },
    [dispatch],
  )

  const onSlugChange = useCallback(
    (value: string) => {
      dispatch(agencyFormFieldChanged({ field: 'slug', value }))
    },
    [dispatch],
  )

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const validation = validateAgencyCreate(fields)
      if (!validation.ok) {
        dispatch(agencyFormValidationFailed(validation.fieldErrors))
        return
      }

      dispatch(agencyFormSubmitted())
      const result = await createAgency(validation.value)

      if (result.error) {
        dispatch(agencyFormFailed(getAuthErrorMessage(result.error)))
        return
      }

      dispatch(agencyFormSucceeded())
    },
    [createAgency, dispatch, fields],
  )

  return {
    isOpen,
    name: fields.name,
    slug: fields.slug,
    fieldErrors,
    submitError,
    isSubmitting,
    openCreate,
    closeCreate,
    onNameChange,
    onSlugChange,
    onSubmit,
  }
}
